import { DatePicker } from "@/components/kit";
import { cn } from "@/lib/utils";
import type { ReportDateBasis } from "@/modules/reports/domain/constants";
import {
  buildCustomDateRange,
  type ReportDatePreset,
  type ReportDateRangeState,
  reportRangeToInclusiveEnd,
  reportRangeToPickerValue,
} from "@/modules/reports/domain/date-range-presets";

type ReportsDateRangeProps = {
  range: ReportDateRangeState;
  activePreset: ReportDatePreset;
  onPresetChange: (preset: ReportDatePreset) => void;
  onBasisChange: (basis: ReportDateBasis) => void;
  onCustomRangeChange: (range: ReportDateRangeState) => void;
};

const PRESETS: Array<{ id: ReportDatePreset; label: string }> = [
  { id: "today", label: "ມື້ນີ້" },
  { id: "7d", label: "7 ວັນ" },
  { id: "month", label: "ເດືອນນີ້" },
  { id: "custom", label: "ກຳນົດເອງ" },
];

const BASIS_OPTIONS: Array<{ id: ReportDateBasis; label: string }> = [
  { id: "created", label: "ວັນສ້າງອໍເດີ້" },
  { id: "shipped", label: "ວັນສົ່ງ" },
];

function formatRangeLabel(range: ReportDateRangeState) {
  const fromLabel = new Date(range.from).toLocaleDateString("lo-LA");
  const toLabel = reportRangeToInclusiveEnd(range.to).toLocaleDateString(
    "lo-LA",
  );
  const basisLabel = range.basis === "shipped" ? "ນັບອໍເດີ້ສົ່ງແລ້ວ" : "ນັບອໍເດີ້ຢືນຢັນ";
  return { fromLabel, toLabel, basisLabel };
}

export function ReportsDateRange({
  range,
  activePreset,
  onPresetChange,
  onBasisChange,
  onCustomRangeChange,
}: ReportsDateRangeProps) {
  const { fromLabel, toLabel, basisLabel } = formatRangeLabel(range);
  const pickerValue = reportRangeToPickerValue(range);

  return (
    <div className="brand-card flex flex-col gap-4 p-4 sm:p-5">
      <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
        <div className="flex flex-col gap-3">
          <span className="font-medium text-foreground text-sm">ຊ່ວງເວລາ</span>
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => onPresetChange(p.id)}
                className={cn(
                  "rounded-full border px-4 py-1.5 font-medium text-sm transition-colors",
                  activePreset === p.id
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-background text-foreground hover:bg-muted/60",
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
          <DatePicker
            mode="range"
            className="w-full rounded-full sm:max-w-md"
            placeholder="ເລືອກວັນເລີ່ມ – ວັນສິ້ນສຸດ"
            captionLayout="dropdown"
            fromYear={2024}
            toYear={new Date().getFullYear() + 1}
            value={pickerValue}
            onChange={(selected) => {
              if (!selected?.from || !selected?.to) return;
              onCustomRangeChange(
                buildCustomDateRange(selected.from, selected.to, range.basis),
              );
            }}
          />
        </div>

        <div className="flex flex-col gap-2 lg:items-end">
          <span className="font-medium text-foreground text-sm">ນັບຕາມ</span>
          <div className="inline-flex w-full flex-wrap gap-1 rounded-full border border-border bg-muted/30 p-1 sm:w-auto">
            {BASIS_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => onBasisChange(opt.id)}
                className={cn(
                  "flex-1 rounded-full px-3 py-1.5 text-center font-medium text-sm transition-colors sm:flex-none sm:px-4",
                  range.basis === opt.id
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-secondary/15 border-t pt-3 text-muted-foreground text-xs">
        <span>
          ສະແດງ: <span className="text-foreground">{fromLabel}</span> –{" "}
          <span className="text-foreground">{toLabel}</span>
        </span>
        <span className="hidden text-border sm:inline">·</span>
        <span>{basisLabel}</span>
      </div>
    </div>
  );
}
